/**
 * P2 Enterprise Analytics - Phase 1
 * 
 * QC Analytics Dashboard Component
 * - Rejection breakdown by category (bar chart)
 * - SLA compliance visualization (percentage + summary)
 * - QC volume trend (line chart)
 * - Date range selector
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Calendar,
  RefreshCw,
  Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * CSV Export Utility
 * Converts data array to CSV and triggers browser download
 */
const exportToCSV = (data, headers, filename) => {
  if (!data || data.length === 0) {
    toast.error('No data available to export');
    return;
  }

  // Build CSV content
  const csvRows = [];
  
  // Add header row
  csvRows.push(headers.map(h => h.label).join(','));
  
  // Add data rows
  data.forEach(row => {
    const values = headers.map(h => {
      const value = row[h.key];
      // Escape commas and quotes in values
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    });
    csvRows.push(values.join(','));
  });

  const csvContent = csvRows.join('\n');
  
  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  // Jun 14, 2026 — FILES-TOAST-001: removed CSV export success toast.
  // Per UX sweep: download actions self-confirm via the browser's
  // native download indicator; redundant toasts create noise.
};

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Date range presets
const DATE_RANGES = [
  { id: '7d', label: 'Last 7 Days', days: 7 },
  { id: '30d', label: 'Last 30 Days', days: 30 },
  { id: '90d', label: 'Last 90 Days', days: 90 }
];

// Category colors for charts
const CATEGORY_COLORS = {
  missing_tax_data: '#EF4444',      // red
  incorrect_calculation: '#F59E0B', // amber
  jurisdiction_error: '#8B5CF6',    // purple
  formatting_issues: '#3B82F6',     // blue
  missing_signatures: '#EC4899',    // pink
  data_mismatch: '#10B981',         // green
  incomplete_search: '#6366F1',     // indigo
  other: '#6B7280'                  // gray
};

const QCAnalytics = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [rejectionData, setRejectionData] = useState({ total_rejections: 0, breakdown: [] });
  const [slaData, setSlaData] = useState({ total_completed: 0, within_sla: 0, outside_sla: 0, compliance_percentage: 0 });
  const [volumeData, setVolumeData] = useState({ trend: [], totals: { submitted: 0, approved: 0, rejected: 0 } });

  const getDateRange = useCallback(() => {
    const preset = DATE_RANGES.find(r => r.id === dateRange);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (preset?.days || 30));
    
    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  }, [dateRange]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const { start_date, end_date } = getDateRange();
    
    try {
      // Fetch all analytics in parallel
      const [rejectionRes, slaRes, volumeRes] = await Promise.all([
        fetch(`${API}/qc/analytics/rejection-breakdown?start_date=${start_date}&end_date=${end_date}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/qc/analytics/sla-compliance?start_date=${start_date}&end_date=${end_date}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/qc/analytics/volume-trend?start_date=${start_date}&end_date=${end_date}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (rejectionRes.ok) {
        const data = await rejectionRes.json();
        setRejectionData(data);
      }
      
      if (slaRes.ok) {
        const data = await slaRes.json();
        setSlaData(data);
      }
      
      if (volumeRes.ok) {
        const data = await volumeRes.json();
        setVolumeData(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Format category name for display
  const formatCategory = (category) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Export Handlers
  const handleExportRejections = () => {
    const data = rejectionData.breakdown.map(item => ({
      category: formatCategory(item.category),
      count: item.count,
      percentage: ((item.count / rejectionData.total_rejections) * 100).toFixed(1)
    }));
    
    const headers = [
      { key: 'category', label: 'Rejection Category' },
      { key: 'count', label: 'Count' },
      { key: 'percentage', label: 'Percentage (%)' }
    ];
    
    exportToCSV(data, headers, 'rejection_breakdown');
  };

  const handleExportSLA = () => {
    const data = [{
      total_completed: slaData.total_completed,
      within_sla: slaData.within_sla,
      outside_sla: slaData.outside_sla,
      compliance_percentage: slaData.compliance_percentage,
      sla_threshold_hours: slaData.sla_threshold_hours
    }];
    
    const headers = [
      { key: 'total_completed', label: 'Total Completed' },
      { key: 'within_sla', label: 'Within SLA' },
      { key: 'outside_sla', label: 'Outside SLA' },
      { key: 'compliance_percentage', label: 'Compliance (%)' },
      { key: 'sla_threshold_hours', label: 'SLA Threshold (Hours)' }
    ];
    
    exportToCSV(data, headers, 'sla_compliance');
  };

  const handleExportVolume = () => {
    const data = volumeData.trend.map(day => ({
      date: day.date,
      submitted: day.submitted,
      approved: day.approved,
      rejected: day.rejected,
      total: day.submitted + day.approved + day.rejected
    }));
    
    const headers = [
      { key: 'date', label: 'Date' },
      { key: 'submitted', label: 'Submitted' },
      { key: 'approved', label: 'Approved' },
      { key: 'rejected', label: 'Rejected' },
      { key: 'total', label: 'Total' }
    ];
    
    exportToCSV(data, headers, 'qc_volume_trend');
  };

  // Get max value for bar chart scaling
  const maxRejectionCount = Math.max(...rejectionData.breakdown.map(b => b.count), 1);

  return (
    <div className="space-y-6" data-testid="qc-analytics">
      {/* Header with Date Range Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">QC Analytics</h2>
          <p className="text-sm text-gray-500">Performance metrics and insights</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date Range Buttons */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {DATE_RANGES.map(range => (
              <button
                key={range.id}
                onClick={() => setDateRange(range.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  dateRange === range.id 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`date-range-${range.id}`}
              >
                {range.label}
              </button>
            ))}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAnalytics}
            disabled={loading}
            className="flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SLA Compliance Card */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5" data-testid="sla-compliance-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">SLA Compliance</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleExportSLA}
                disabled={slaData.total_completed === 0}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                data-testid="export-sla-btn"
              >
                <Download className="w-4 h-4" />
                <span className="text-xs">CSV</span>
              </Button>
            </div>
            
            {slaData.total_completed > 0 ? (
              <>
                {/* Large Percentage Display */}
                <div className="text-center mb-4">
                  <div className={`text-5xl font-bold ${
                    slaData.compliance_percentage >= 90 ? 'text-green-600' :
                    slaData.compliance_percentage >= 70 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {slaData.compliance_percentage}%
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Reviews within {slaData.sla_threshold_hours}h SLA
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden mb-4">
                  <div 
                    className={`absolute left-0 top-0 h-full transition-all ${
                      slaData.compliance_percentage >= 90 ? 'bg-green-500' :
                      slaData.compliance_percentage >= 70 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${slaData.compliance_percentage}%` }}
                  />
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-semibold text-gray-900">{slaData.total_completed}</div>
                    <div className="text-xs text-gray-500">Total Completed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-green-600">{slaData.within_sla}</div>
                    <div className="text-xs text-gray-500">Within SLA</div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-red-600">{slaData.outside_sla}</div>
                    <div className="text-xs text-gray-500">Outside SLA</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No completed QC reviews in this period</p>
              </div>
            )}
          </div>

          {/* Volume Trend Summary Card */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5" data-testid="volume-trend-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">QC Volume</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleExportVolume}
                disabled={volumeData.trend.length === 0}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                data-testid="export-volume-btn"
              >
                <Download className="w-4 h-4" />
                <span className="text-xs">CSV</span>
              </Button>
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-700">{volumeData.totals.submitted}</div>
                <div className="text-xs text-blue-600">Submitted</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">{volumeData.totals.approved}</div>
                <div className="text-xs text-green-600">Approved</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-700">{volumeData.totals.rejected}</div>
                <div className="text-xs text-red-600">Rejected</div>
              </div>
            </div>
            
            {/* Mini Trend Chart */}
            {volumeData.trend.length > 0 && (
              <div className="h-24 flex items-end gap-1">
                {volumeData.trend.slice(-14).map((day, index) => {
                  const total = day.submitted + day.approved + day.rejected;
                  const maxTotal = Math.max(...volumeData.trend.slice(-14).map(d => d.submitted + d.approved + d.rejected), 1);
                  const height = total > 0 ? Math.max((total / maxTotal) * 100, 10) : 5;
                  
                  return (
                    <div
                      key={day.date}
                      className="flex-1 bg-purple-200 hover:bg-purple-300 rounded-t transition-colors cursor-pointer relative group"
                      style={{ height: `${height}%` }}
                      title={`${day.date}: ${total} total`}
                    >
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                        {day.date.slice(5)}: {day.submitted}↑ {day.approved}✓ {day.rejected}✗
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="text-xs text-gray-400 text-center mt-2">Last 14 days trend</div>
          </div>

          {/* Rejection Breakdown Card - Full Width */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 lg:col-span-2" data-testid="rejection-breakdown-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-gray-900">Rejection Breakdown</h3>
                <span className="text-sm text-gray-500">{rejectionData.total_rejections} total rejections</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleExportRejections}
                disabled={rejectionData.breakdown.length === 0}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                data-testid="export-rejections-btn"
              >
                <Download className="w-4 h-4" />
                <span className="text-xs">CSV</span>
              </Button>
            </div>
            
            {rejectionData.breakdown.length > 0 ? (
              <div className="space-y-3">
                {rejectionData.breakdown.map((item, index) => {
                  const percentage = ((item.count / rejectionData.total_rejections) * 100).toFixed(1);
                  const barWidth = (item.count / maxRejectionCount) * 100;
                  const color = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;
                  
                  return (
                    <div key={item.category} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {formatCategory(item.category)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {item.count} ({percentage}%)
                        </span>
                      </div>
                      <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all group-hover:opacity-80"
                          style={{ 
                            width: `${barWidth}%`,
                            backgroundColor: color
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
                <p>No rejections in this period</p>
                <p className="text-sm">Great work!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QCAnalytics;
